import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Icon from "@/components/Icon";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 + i * 0.12 },
  }),
};

function MaskedLine({ children, i, className }) {
  return (
    <span className="block overflow-hidden pt-[0.08em] pb-[0.18em]">
      <motion.span
        className={"block " + (className || "")}
        variants={lineReveal}
        custom={i}
        initial="hidden"
        animate="visible"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ property }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.7]);

  const name = property?.name || "Casa Via Solana";
  const address = property?.address || "48205 Via Solana, La Quinta, CA";
  const tagline = property?.tagline || "A Desert Modern Retreat";
  const hero = property?.hero_image;

  return (
    <section ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {hero && (
          <img src={hero} alt={name} className="h-full w-full object-cover" />
        )}
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#2C3325]" />

      <div className="relative z-10 h-full flex flex-col justify-between px-6 sm:px-10 lg:px-16 py-10 text-[#F7F6F2]">
        <div className="h-6" aria-hidden />

        <div className="max-w-5xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="subhead text-[#F7F6F2] text-lg md:text-xl mb-5"
          >
            {tagline}
          </motion.p>
          <h1 className="font-display font-medium text-[10vw] sm:text-[7.5vw] lg:text-[6vw] leading-[0.9] tracking-tight whitespace-nowrap">
            <MaskedLine i={0}>{name}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="mt-8 text-base sm:text-lg text-[#F7F6F2]/85 max-w-md"
          >
            {address}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="flex items-center gap-3 text-[#F7F6F2]/70"
        >
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          >
            <Icon name="SunHorizon" size={22} weight="duotone" />
          </motion.span>
          <span className="text-xs tracking-widest uppercase">Scroll to begin</span>
        </motion.div>
      </div>
    </section>
  );
}
